import type { CancelTokenSource } from 'axios';
import { create } from 'zustand';

import { sendDepositAnalytics } from 'lib/gtm';
import { useAuthStore } from 'lib/stores/authStore';
import type { ServerError } from 'models';
import type { PlaidLinkType } from 'models/plaid';
import type { DepositDestination, WithdrawalSource } from 'models/reqres';
import type { InitiatePlaidDepositResponse } from 'models/reqres';
import type { TransferVo } from 'models/transfer';
import { refreshAfterAccountLinked, seedLinkedBankAccountInCache } from 'queries/bankAccounts';
import { getHasDepositedBefore, appendTransferToCache, getBankTransfersFromCache } from 'queries/bankTransfers';
import { updateUserProperties } from 'queries/userProperties';
import { useAppNavigationStore } from 'stores/zustand/appNavigationStore';
import { useBankLinkingAeropayDialogStore } from 'stores/zustand/bankLinkingAeropayDialogStore';
import { useBankLinkingPlaidDialogStore } from 'stores/zustand/bankLinkingPlaidDialogStore';
import { useDepositDialogStore } from 'stores/zustand/depositDialogStore';
import { useMessageDetailDialogStore } from 'stores/zustand/messageDetailDialogStore';
import { FundMethods, useRegistrationStore } from 'stores/zustand/registrationStore';
import { useSnackbarStore } from 'stores/zustand/snackbarStore';
import { useTransferDialogStore } from 'stores/zustand/transferDialogStore';
import { useWithdrawalDialogStore } from 'stores/zustand/withdrawalDialogStore';
import config from 'utils/config';
import { buildDepositTrackingContext, getDepositRequestTrackingProps, trackDepositStep } from 'utils/depositTracking';
import { getRiskifiedSessionId } from 'utils/riskified';
import { fetchSardineSessionKey } from 'utils/sardine';
import { handleTransferError } from 'utils/transfers';
import { uuidv4 } from 'utils/uuid';

import {
    createAeropayLinkToken,
    createDeposit,
    createPlaidLinkToken,
    createWithdrawal,
    getPlaidImmediateFundingDetails,
    initiatePlaidDeposit as initiatePlaidDepositRequest,
    initiatePlaidWithdrawal as initiatePlaidWithdrawalRequest,
    linkAeropayAccount as linkAeropayAccountRequest,
    linkPlaidAccount as linkPlaidAccountRequest,
} from '../../transport/domains/banking';
import { noop } from '../../utils/noop';

// Ephemeral bank-linking + deposit/withdrawal flow state — the imperative
// counterpart of the RQ-owned account/transfer data in queries/bankAccounts.ts
// and queries/bankTransfers.ts. Follows the same shape as paymentsFlowStore /
// cryptoFlowStore.

// Non-reactive imperative handles — no component renders off these, so they
// live at module scope. Aeropay and Plaid link-token issuance share a single
// in-flight request slot, matching the old BanksStore (only one link flow is
// ever active at a time).
let tokenTimer: NodeJS.Timeout | null = null;
let tokenFetch: CancelTokenSource | null = null;

interface BankFlowState {
    aeropayToken: string;
    plaidToken: string;
    plaidLinkType: PlaidLinkType | undefined;
    plaidImmediateDepositAmountCents: number | null;
    plaidImmediateFundingIsPartial: boolean;
    linkingAccount: boolean;
    linkedAccount: boolean;
    onLinkAccountSuccess: ((bankId?: string) => void) | undefined;
    actions: {
        setAeropayToken: (token: string) => void;
        clearAeropayToken: () => void;
        setPlaidToken: (token: string) => void;
        setPlaidLinkType: (linkType: PlaidLinkType | undefined) => void;
        setPlaidImmediateDepositAmountCents: (amountCents: number | null) => void;
        setPlaidImmediateFundingIsPartial: (isPartial: boolean) => void;
        clearPlaidToken: () => void;
        setLinkingAccounts: (linking: boolean) => void;

        getAeropayLinkToken: (
            userId: string,
            onLinkAccountSuccess?: (bankId?: string) => void,
            onErr?: (err: ServerError) => void,
        ) => void;
        linkAeropayAccount: (
            userId: string,
            aeropayUserId: string,
            aeropayUserPassword: string,
            onSuc: () => void,
            onErr?: (err: ServerError) => void,
        ) => void;

        getPlaidLinkToken: (userId: string, linkType: PlaidLinkType, onErr?: (err: ServerError) => void) => void;
        prefetchPlaidLinkToken: (userId: string, linkType: PlaidLinkType, onErr?: (err: ServerError) => void) => void;
        linkPlaidAccount: (
            linkType: PlaidLinkType,
            publicToken: string,
            onSuc: () => void,
            onErr?: (err: ServerError) => void,
        ) => void;
        relinkPlaidAccount: (linkToken: string, amountCents: number) => void;
        initiatePlaidRFPTransactionFlow: (linkToken: string) => void;
        getPlaidImmediateFundingAvailable: (
            bankId: string,
            amountCents: number,
            onSuc?: () => void,
            onErr?: (err: ServerError) => void,
        ) => Promise<void>;
        initiatePlaidDeposit: (
            bankId: string,
            amountCents: number,
            onSuc: (transfer?: TransferVo) => void,
            onErr?: (err: ServerError) => void,
            destination?: DepositDestination,
        ) => Promise<void>;
        initiatePlaidWithdrawal: (
            bankId: string,
            amountCents: number,
            mfaCode: string,
            onSuc: (transfer?: TransferVo) => void,
            onErr?: (err: ServerError) => void,
            source?: WithdrawalSource,
        ) => Promise<void>;

        createTransfer: (
            bankId: string,
            amountCents: number,
            isDeposit: boolean,
            onSuc: (transfer?: TransferVo) => void,
            onErr?: (errorTitle?: string, errorMessage?: string, errorCode?: string) => void,
            mfaCode?: string,
            destination?: DepositDestination,
            source?: WithdrawalSource,
        ) => Promise<void>;

        disposeAeropayLinkToken: () => void;
        disposePlaidLinkToken: () => void;
        dispose: () => void;
    };
}

function trackProviderOpenStartedForPlaid(): void {
    const userId = useAuthStore.getState().userId ?? '';
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    trackDepositStep(
        'providerOpenStarted',
        buildDepositTrackingContext({
            paymentMethod: 'bank',
            provider: 'plaid',
            pathname,
            pageLoadId: useAppNavigationStore.getState().pageLoadId,
            isSignup: pathname.includes('/sign-up'),
            hasDepositedBefore: getHasDepositedBefore(getBankTransfersFromCache(userId)),
        }),
    );
}

// Snapshots hasDepositedBefore before the new transfer is appended to the
// cache, then fires deposit analytics — ported from BanksStore.onDepositCreated.
function recordDepositCreated(userId: string, amountCents: number, transferVo?: TransferVo): void {
    const isFirstDeposit = !getHasDepositedBefore(getBankTransfersFromCache(userId));
    sendDepositAnalytics(amountCents / 100, FundMethods.BANK, transferVo?.id ?? uuidv4(), isFirstDeposit);

    if (transferVo) {
        appendTransferToCache(userId, transferVo, true);
    }
}

// The Plaid deposit response carries a flat transfer_id, not a deposit object.
// Callers only read `id` (and `amount_cents`, which they already fall back on),
// so surface the id in the shape they expect rather than reshaping every caller.
function plaidDepositTransfer(response: InitiatePlaidDepositResponse): TransferVo | undefined {
    if (response.deposit) return response.deposit;
    if (!response.transfer_id) return undefined;
    return { id: response.transfer_id } as TransferVo;
}

export const useBankFlowStore = create<BankFlowState>()((set, get) => ({
    aeropayToken: '',
    plaidToken: '',
    plaidLinkType: undefined,
    plaidImmediateDepositAmountCents: null,
    plaidImmediateFundingIsPartial: false,
    linkingAccount: false,
    linkedAccount: false,
    onLinkAccountSuccess: undefined,

    actions: {
        setAeropayToken: (aeropayToken) => set({ aeropayToken }),
        clearAeropayToken: () => set({ aeropayToken: '' }),
        setPlaidToken: (plaidToken) => set({ plaidToken }),
        setPlaidLinkType: (plaidLinkType) => set({ plaidLinkType }),
        setPlaidImmediateDepositAmountCents: (plaidImmediateDepositAmountCents) =>
            set({ plaidImmediateDepositAmountCents }),
        setPlaidImmediateFundingIsPartial: (plaidImmediateFundingIsPartial) => set({ plaidImmediateFundingIsPartial }),
        clearPlaidToken: () => set({ plaidToken: '', plaidLinkType: undefined }),
        setLinkingAccounts: (linkingAccount) => set({ linkingAccount }),

        getAeropayLinkToken: (userId, onLinkAccountSuccess, onErr) => {
            set({ linkingAccount: true });
            if (tokenFetch !== null) return;
            const messageDetailDialogActions = useMessageDetailDialogStore.getState().actions;
            tokenFetch = createAeropayLinkToken(
                userId,
                (token) => {
                    set({ aeropayToken: token, onLinkAccountSuccess });
                    tokenTimer = setInterval(() => {
                        get().actions.disposeAeropayLinkToken();
                    }, config.tokenExpiration);
                },
                (err) => {
                    // Close the bank linking dialog on error
                    useBankLinkingAeropayDialogStore.getState().actions.close();

                    onErr?.(err);

                    if (err?.error.message && err?.error.details) {
                        messageDetailDialogActions.setTitle(err.error.message);
                        messageDetailDialogActions.setDetails(err.error.details);
                        messageDetailDialogActions.open();
                        return;
                    }

                    useSnackbarStore.getState().actions.pushMessage({
                        text: 'There was an error linking your account. Please try again.',
                        variant: 'negative',
                    });
                },
                () => {
                    tokenFetch = null;
                    set({ linkingAccount: false });
                },
            );
        },

        linkAeropayAccount: (userId, aeropayUserId, aeropayUserPassword, onSuc, onErr = noop) => {
            linkAeropayAccountRequest(
                userId,
                { aeropay_user_id: aeropayUserId, aeropay_user_password: aeropayUserPassword },
                (account) => {
                    updateUserProperties({ completedLinkingOnboarding: true });
                    seedLinkedBankAccountInCache(userId, account, 'aeropay');
                    void refreshAfterAccountLinked(userId, 'aeropay');
                    get().onLinkAccountSuccess?.(account.bank_id);
                    onSuc();

                    set({
                        linkingAccount: false,
                        linkedAccount: true,
                        aeropayToken: '',
                        onLinkAccountSuccess: undefined,
                    });
                },
                (err) => {
                    set({ linkingAccount: false, aeropayToken: '', onLinkAccountSuccess: undefined });
                    onErr(err);
                },
            );
        },

        getPlaidLinkToken: (userId, linkType, onErr) => {
            set({ linkingAccount: true, plaidLinkType: linkType });
            if (tokenFetch !== null) return;

            tokenFetch = createPlaidLinkToken(
                userId,
                linkType,
                (token) => {
                    set({ plaidToken: token });
                    tokenTimer = setInterval(() => {
                        get().actions.disposePlaidLinkToken();
                    }, config.tokenExpiration);
                },
                (err) => {
                    tokenFetch = null;
                    onErr?.(err);
                },
                () => {
                    tokenFetch = null;
                    set({ linkingAccount: false });
                },
            );
        },

        prefetchPlaidLinkToken: (userId, linkType, onErr) => {
            if (tokenFetch !== null || get().plaidToken) return;

            set({ plaidLinkType: linkType });

            tokenFetch = createPlaidLinkToken(
                userId,
                linkType,
                (token) => {
                    set({ plaidToken: token });
                    tokenTimer = setInterval(() => {
                        get().actions.disposePlaidLinkToken();
                    }, config.tokenExpiration);
                },
                (err) => {
                    tokenFetch = null;
                    onErr?.(err);
                },
                () => {
                    tokenFetch = null;
                },
            );
        },

        linkPlaidAccount: (linkType, publicToken, onSuc, onErr = noop) => {
            const userId = useAuthStore.getState().userId ?? '';
            linkPlaidAccountRequest(
                userId,
                { link_type: linkType, public_token: publicToken },
                (account) => {
                    updateUserProperties({ completedLinkingOnboarding: true });
                    seedLinkedBankAccountInCache(userId, account, 'plaid');
                    void refreshAfterAccountLinked(userId, 'plaid');
                    get().onLinkAccountSuccess?.(account.bank_id);
                    onSuc();

                    set({
                        linkingAccount: false,
                        linkedAccount: true,
                        plaidToken: '',
                        plaidLinkType: undefined,
                        onLinkAccountSuccess: undefined,
                    });
                },
                (err) => {
                    set({
                        linkingAccount: false,
                        plaidToken: '',
                        plaidLinkType: undefined,
                        onLinkAccountSuccess: undefined,
                    });
                    onErr(err);
                },
            );
        },

        relinkPlaidAccount: (linkToken, amountCents) => {
            set({
                plaidToken: linkToken,
                plaidLinkType: 'link', // Relinking always uses standard link, not layer
                onLinkAccountSuccess: () => {
                    useRegistrationStore.getState().actions.setFundMethod(FundMethods.BANK);
                    useDepositDialogStore.getState().actions.setAmount(amountCents / 100);
                    useDepositDialogStore.getState().actions.setStep('prereview');
                    useTransferDialogStore.getState().actions.depositOpened();
                },
            });

            useDepositDialogStore.setState({ loading: false });
            useTransferDialogStore.getState().actions.dialogClosed();
            trackProviderOpenStartedForPlaid();
            useBankLinkingPlaidDialogStore.getState().actions.open();
        },

        initiatePlaidRFPTransactionFlow: (linkToken) => {
            set({ plaidToken: linkToken, plaidLinkType: 'link' }); // RFP transaction flow uses standard link, not layer
            trackProviderOpenStartedForPlaid();
            useBankLinkingPlaidDialogStore.getState().actions.open();
        },

        getPlaidImmediateFundingAvailable: async (bankId, amountCents, onSuc = noop, onErr = noop) => {
            // Re-arm the review step's loading gate and clear any stale partial flag
            // from a previous deposit attempt.
            set({ plaidImmediateDepositAmountCents: null, plaidImmediateFundingIsPartial: false });
            const userId = useAuthStore.getState().userId ?? '';
            const riskifiedCartToken = getRiskifiedSessionId();
            const sardineSessionKey = await fetchSardineSessionKey('deposit', userId);
            getPlaidImmediateFundingDetails(
                userId,
                bankId,
                amountCents,
                (response) => {
                    // The backend includes a link_token whenever the account needs to be
                    // re-linked, even without status === 'relinking' — treat its presence
                    // as the relink signal so the user isn't sent on to the funding screens.
                    if (!!response.link_token) {
                        get().actions.relinkPlaidAccount(response.link_token, amountCents);
                        return;
                    }

                    set({
                        plaidImmediateDepositAmountCents: response.amount_cents,
                        plaidImmediateFundingIsPartial: response.is_partial ?? false,
                    });
                    onSuc();
                },
                (err) => {
                    onErr(err);
                },
                noop,
                riskifiedCartToken,
                sardineSessionKey,
            );
        },

        initiatePlaidDeposit: async (bankId, amountCents, onSuc, onErr = noop, destination) => {
            const userId = useAuthStore.getState().userId ?? '';
            const riskifiedCartToken = getRiskifiedSessionId();
            const sardineSessionKey = await fetchSardineSessionKey('deposit', userId);
            initiatePlaidDepositRequest(
                userId,
                {
                    bank_id: bankId,
                    amount_cents: amountCents,
                    riskified_cart_token: riskifiedCartToken || undefined,
                    sardine_session_key: sardineSessionKey || undefined,
                    destination,
                    ...getDepositRequestTrackingProps(),
                },
                (response) => {
                    // Check if needs to re-link
                    if (response.status === 'relinking' && !!response.link_token) {
                        get().actions.relinkPlaidAccount(response.link_token, amountCents);
                        return;
                    }

                    // Check if should redirect to RfP transactional flow
                    if (response.status === 'rfp_completion_required' && !!response.link_token) {
                        get().actions.initiatePlaidRFPTransactionFlow(response.link_token);
                        return;
                    }

                    recordDepositCreated(userId, amountCents, response.deposit);
                    onSuc(plaidDepositTransfer(response));
                },
                (err) => {
                    onErr(err);
                },
            );
        },

        initiatePlaidWithdrawal: async (bankId, amountCents, mfaCode, onSuc, onErr = noop, source) => {
            const userId = useAuthStore.getState().userId ?? '';
            const sardineSessionKey = await fetchSardineSessionKey('withdrawal', userId);
            initiatePlaidWithdrawalRequest(
                userId,
                {
                    bank_id: bankId,
                    amount_cents: amountCents,
                    mfa_code: mfaCode,
                    sardine_session_key: sardineSessionKey || undefined,
                    source,
                },
                (response) => {
                    // Check if needs to re-link
                    if (response.status === 'relinking' && !!response.link_token) {
                        get().actions.relinkPlaidAccount(response.link_token, amountCents);
                        return;
                    }

                    if (response.withdrawal) {
                        appendTransferToCache(userId, response.withdrawal, false);
                    }
                    onSuc(response.withdrawal);
                },
                onErr,
            );
        },

        createTransfer: async (bankId, amountCents, isDeposit, onSuc, onErr = noop, mfaCode, destination, source) => {
            const userId = useAuthStore.getState().userId ?? '';
            if (isDeposit) {
                const sardineSessionKey = await fetchSardineSessionKey('deposit', userId);
                createDeposit(
                    userId,
                    {
                        amount_cents: amountCents,
                        bank_id: bankId,
                        fee_cents: 0,
                        sardine_session_key: sardineSessionKey || undefined,
                        destination,
                        ...getDepositRequestTrackingProps(),
                    },
                    (response) => {
                        recordDepositCreated(userId, amountCents, response.deposit);
                        onSuc(response.deposit);
                    },
                    (err) => onErr(...transferErrorArgs(err, true)),
                );
            } else {
                const sardineSessionKey = await fetchSardineSessionKey('withdrawal', userId);
                createWithdrawal(
                    userId,
                    {
                        amount_cents: amountCents,
                        bank_id: bankId,
                        fee_cents: config.withdrawalFee,
                        mfa_code: mfaCode || '',
                        sardine_session_key: sardineSessionKey || undefined,
                        source,
                    },
                    (response) => {
                        if (response.withdrawal) {
                            appendTransferToCache(userId, response.withdrawal, false);
                        }
                        onSuc(response.withdrawal);
                    },
                    (err) => onErr(...transferErrorArgs(err, false)),
                );
            }
        },

        disposeAeropayLinkToken: () => {
            if (tokenTimer !== null) {
                clearInterval(tokenTimer);
            }
            set({ aeropayToken: '', onLinkAccountSuccess: undefined });
        },

        disposePlaidLinkToken: () => {
            if (tokenTimer !== null) {
                clearInterval(tokenTimer);
            }
            set({ plaidToken: '', plaidLinkType: undefined });
        },

        dispose: () => {
            get().actions.disposeAeropayLinkToken();
            get().actions.disposePlaidLinkToken();
            if (tokenFetch !== null) {
                tokenFetch.cancel('Aborting link token create request');
                tokenFetch = null;
            }
            set({ linkingAccount: false, linkedAccount: false });
        },
    },
}));

function transferErrorArgs(
    err: ServerError,
    isDeposit: boolean,
): [errorTitle?: string, errorMessage?: string, errorCode?: string] {
    const { errorTitle, errorMessage } = handleTransferError(err, isDeposit);
    const errorCode = err?.error?.code?.toLowerCase();
    return [errorTitle, errorMessage, errorCode];
}

// Opens the withdrawal dialog on the "why is my cash held" info screen.
// Ported from BanksStore.openWithdrawalHoldInfoModal — stateless, kept
// alongside the rest of the bank flow surface for one import path.
export function openWithdrawalHoldInfoModal(): void {
    useTransferDialogStore.getState().actions.withdrawOpened();
    useWithdrawalDialogStore
        .getState()
        .actions.setFailed(
            'When your cash becomes available to withdraw',
            `Newly deposited funds may be held briefly for regulatory purposes:\n <div><ul style="padding-left: 20px; text-align: justify; list-style: disc"><li style="padding-bottom: 16px;"><b>Bank and card deposits:</b> available after up to 2 days</li><li><b>Bonus funds:</b> available after you've traded the bonus amount</li></ul></div>`,
        );
}
